import React, { useState } from 'react';
import { X, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { reviewsAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

const ReviewModal = ({ order, onClose, onSuccess }) => {
  const { toast } = useToast();
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [textReview, setTextReview] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (rating === 0) {
      toast({
        title: "Rating required",
        description: "Please select a star rating",
        variant: "destructive"
      });
      return;
    }

    setSubmitting(true);
    try {
      const reviewData = {
        order_id: order.id,
        rating,
        text_review: textReview || undefined,
        video_url: videoUrl || undefined
      };

      const response = await reviewsAPI.create(reviewData);
      
      const unlocks = response.data.unlocks_granted;
      toast({
        title: "Review submitted!",
        description: `Thank you! You've earned ${unlocks} purchase unlock${unlocks > 1 ? 's' : ''}.`,
      });
      
      onSuccess();
      onClose();
    } catch (error) {
      toast({
        title: "Submission failed",
        description: error.response?.data?.detail || "Failed to submit review",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getUnlockInfo = () => {
    if (videoUrl) return { type: 'Video', unlocks: 3, color: 'text-purple-600' };
    if (textReview) return { type: 'Written', unlocks: 2, color: 'text-blue-600' };
    return { type: 'Star', unlocks: 1, color: 'text-yellow-600' };
  };

  const unlockInfo = getUnlockInfo();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-2xl font-bold">Submit Your Review</h2>
            <p className="text-sm text-gray-600 mt-1">Order #{order.id.slice(0, 8)}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Unlock Info */}
        <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-700">Earn Purchase Unlocks</p>
              <p className="text-xs text-gray-600 mt-1">
                ⭐ Star = 1 unlock | 📝 Written = 2 unlocks | 🎥 Video = 3 unlocks
              </p>
            </div>
            <div className="text-right">
              <p className={`text-2xl font-bold ${unlockInfo.color}`}>{unlockInfo.unlocks}</p>
              <p className="text-xs text-gray-600">{unlockInfo.type} Review</p>
            </div>
          </div>
        </div>

        {/* Rating */}
        <div className="mb-6">
          <Label className="mb-2 block">Rating *</Label>
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                onClick={() => setRating(star)}
                className="transition-transform hover:scale-110"
              >
                <Star
                  className={`h-8 w-8 ${
                    star <= (hoverRating || rating)
                      ? 'fill-yellow-500 text-yellow-500'
                      : 'text-gray-300'
                  }`}
                />
              </button>
            ))}
          </div>
        </div>

        {/* Text Review */}
        <div className="mb-6">
          <Label htmlFor="textReview" className="mb-2 block">
            Written Review (Optional - Earn 2 unlocks)
          </Label>
          <Textarea
            id="textReview"
            placeholder="Share your experience with this product..."
            value={textReview}
            onChange={(e) => setTextReview(e.target.value)}
            rows={4}
            className="resize-none"
          />
        </div>

        {/* Video URL */}
        <div className="mb-6">
          <Label htmlFor="videoUrl" className="mb-2 block">
            Video Review URL (Optional - Earn 3 unlocks)
          </Label>
          <Input
            id="videoUrl"
            type="url"
            placeholder="https://youtube.com/... or https://drive.google.com/..."
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
          />
          <p className="text-xs text-gray-500 mt-1">
            Upload your video to YouTube, Google Drive, or any platform and paste the link here
          </p>
        </div>

        {/* Order Items */}
        <div className="mb-6">
          <Label className="mb-2 block">Order Items</Label>
          <div className="space-y-2">
            {order.items.map((item, idx) => (
              <div key={idx} className="flex items-center gap-3 p-2 bg-gray-50 rounded">
                <img src={item.product_image} alt={item.product_name} className="w-12 h-12 object-cover rounded" />
                <div className="flex-1">
                  <p className="text-sm font-semibold">{item.product_name}</p>
                  <p className="text-xs text-gray-600">Qty: {item.quantity}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1" disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            className="flex-1 bg-red-600 hover:bg-red-700"
            disabled={submitting}
          >
            {submitting ? 'Submitting...' : 'Submit Review'}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default ReviewModal;
